/*
 * Small wooden bench prop — the stargaze anchor point.
 *
 * Lives between the right-side mushroom house and the well, at the spot the
 * stargaze minigame anchors the player to (475, 470 in world coords by default).
 * Same wood tones as the well roof so it reads as part of the same little
 * carpenter's hand: 0x6b4423 posts, 0x5c3d28 plank.
 *
 * Roughly 30px wide × 20px tall (including legs + backrest). Soft elliptical
 * shadow underneath grounds it on the moss.
 *
 * This is a static prop — no animation, no state. The proximity prompt and the
 * minigame layer are owned elsewhere (see render/stargaze.ts, minigames/stargaze.ts).
 */

import { Container, Graphics } from 'pixi.js';

const WOOD_DARK = 0x5c3d28;
const WOOD_LIGHT = 0x6b4423;

export function createBench(x: number, y: number): Container {
  const root = new Container();
  root.position.set(x, y);

  // Soft elliptical ground shadow — same tonal recipe as the well's stones.
  const shadow = new Graphics();
  shadow.ellipse(0, 10, 20, 5).fill({ color: 0x000000, alpha: 0.18 });
  root.addChild(shadow);

  const bench = new Graphics();

  // Legs (two of them, splayed slightly).
  bench.rect(-12, 0, 2.5, 10).fill(WOOD_DARK);
  bench.rect(9.5, 0, 2.5, 10).fill(WOOD_DARK);

  // Plank seat — slightly proud lighter top edge for a soft highlight.
  bench.rect(-15, -3, 30, 4).fill(WOOD_LIGHT);
  bench.rect(-15, -3, 30, 1).fill({ color: 0x8a5a33, alpha: 0.55 });

  // Back-rest posts.
  bench.rect(-13, -12, 2, 10).fill(WOOD_DARK);
  bench.rect(11, -12, 2, 10).fill(WOOD_DARK);

  // Low backrest plank.
  bench.rect(-13, -12, 26, 2.5).fill(WOOD_LIGHT);

  // Wood-grain hint along the seat (very subtle).
  bench
    .moveTo(-13, -1.5)
    .lineTo(13, -1.5)
    .stroke({ color: 0x000000, alpha: 0.12, width: 0.5 });

  root.addChild(bench);

  return root;
}
